/**
 * Uploader en segundo plano de las grabaciones de backup.
 *
 * Corre desacoplado del ciclo de la llamada: barre IndexedDB periódicamente
 * (y al cargar la app, y cuando vuelve la conexión) buscando grabaciones
 * listas, las ensambla en un único archivo y las sube a api-old. Solo borra
 * de IndexedDB si el backend responde OK; ante cualquier fallo, la grabación
 * permanece y se reintenta en el siguiente ciclo. Esta durabilidad es la red
 * de seguridad de toda la operación subida+encolado del backend.
 *
 * Dos salvaguardas ante un fallo permanente de subida:
 *  - Tope de retención: se descarta una grabación que lleva demasiados días
 *    sin poder subir, para que IndexedDB no crezca sin límite y la cuota no
 *    impida grabar llamadas nuevas.
 *  - Visibilidad: si una grabación falla varias veces seguidas, se emite un
 *    console.error (que LogRocket captura) para que el problema no sea
 *    silencioso — es el equivalente en el navegador a la alarma de la DLQ.
 */

import Vue from 'vue';
import { listRecordings, getChunks, deleteRecording } from './db';

const FLUSH_INTERVAL_MS = 30000;
// Una grabación 'recording' que no recibe chunks nuevos por este tiempo se
// asume interrumpida (crash/cierre) y se sube con lo que haya.
const ORPHAN_MS = 120000;
// Tras estos días sin poder subir, se descarta el backup local (cota de espacio).
const MAX_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;
// Fallos de subida seguidos antes de reportar que una grabación está atascada.
const STUCK_FAILURES = 5;
const ENDPOINT = 'customers/call_v2/recording';

let started = false;
let flushing = false;
// En memoria (por sesión): fallos consecutivos y grabaciones ya reportadas.
const failureCounts = new Map();
const alerted = new Set();

function isUploadable(meta) {
  if (meta.status === 'complete') {
    return true;
  }
  if (meta.status === 'recording' && (Date.now() - (meta.updatedAt || 0)) > ORPHAN_MS) {
    return true;
  }
  return false;
}

async function uploadOne(meta) {
  const chunks = await getChunks(meta.id);
  if (!chunks.length) {
    // Metadata sin datos: nada que subir, limpiar.
    await deleteRecording(meta.id);
    return;
  }

  const blob = new Blob(chunks.map(c => c.blob), { type: meta.mimeType || 'audio/webm' });
  const extension = meta.extension || 'webm';

  const form = new FormData();
  form.append('call_uuid', meta.call_uuid);
  form.append('recorded_at', meta.recorded_at);
  form.append('direction', meta.direction || 'outbound');
  if (meta.company_id) {
    form.append('company_id', meta.company_id);
  }
  form.append('audio', blob, `${meta.call_uuid}.${extension}`);

  await Vue.axios.post(ENDPOINT, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  // El backend respondió 200 (subió a S3 y encoló) => ya se puede borrar.
  await deleteRecording(meta.id);
}

export async function flushBackups() {
  if (flushing || typeof indexedDB === 'undefined') {
    return;
  }
  flushing = true;
  try {
    const all = await listRecordings();
    for (let i = 0; i < all.length; i += 1) {
      const meta = all[i];

      // Tope de retención: si lleva demasiados días sin subir, se descarta para
      // no crecer sin límite (y que la cuota no impida grabar llamadas nuevas).
      if (meta.createdAt && (Date.now() - meta.createdAt) > MAX_RETENTION_MS) {
        console.error('backupUploader: descartando backup vencido sin subir', meta.id, meta.call_uuid);
        // eslint-disable-next-line no-await-in-loop
        await deleteRecording(meta.id).catch(() => {});
        failureCounts.delete(meta.id);
        alerted.delete(meta.id);
        continue;
      }

      if (!isUploadable(meta)) {
        continue;
      }

      try {
        // Secuencial a propósito: no saturar la red con varios uploads a la vez.
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(meta);
        // Subió: limpiar el rastro de fallos.
        failureCounts.delete(meta.id);
        alerted.delete(meta.id);
      } catch (e) {
        // Se reintenta en el próximo ciclo; el backup permanece en IndexedDB.
        const count = (failureCounts.get(meta.id) || 0) + 1;
        failureCounts.set(meta.id, count);
        console.warn('backupUploader: fallo subiendo', meta.id, `(intento ${count})`, e && e.message);

        // Visibilidad: si insiste en fallar, un console.error (lo captura
        // LogRocket) una sola vez por sesión para no inundar.
        if (count >= STUCK_FAILURES && !alerted.has(meta.id)) {
          alerted.add(meta.id);
          console.error(
            'backupUploader: backup atascado, no se pudo subir tras varios intentos',
            meta.id, meta.call_uuid, e && e.message,
          );
        }
      }
    }
  } catch (e) {
    console.warn('backupUploader: error listando grabaciones', e);
  } finally {
    flushing = false;
  }
}

export function startBackupUploader() {
  if (started || typeof indexedDB === 'undefined') {
    return;
  }
  started = true;
  // Primer barrido al cargar: recupera backups de sesiones anteriores o crashes.
  flushBackups();
  setInterval(flushBackups, FLUSH_INTERVAL_MS);
  // Reintentar en cuanto vuelva la conexión.
  window.addEventListener('online', flushBackups);
}



// WEBPACK FOOTER //
// ./src/services/callRecording/uploader.js